import { createContext, useState, ReactNode, useEffect } from 'react';
import { Creative, StatusAction } from 'lib.store/src/type-organizer/creatives/models';
import { findNextCreative } from '../helpers';
import {
  useGetCreativesQuery,
  usePatchCreativeStatusAllMutation,
  usePatchCreativeStatusByIdMutation,
} from 'lib.store/src/type-organizer/creatives/creatives.api';
import { useParams } from 'react-router-dom';
import { ErrorDataResponse } from 'lib.shared/src/utils';
import { notification } from 'antd';
interface CreativeTemplatesCtxType {
  selectedCreative: Creative | null;
  setSelectedCreative: (creative: Creative) => void;
  loading: boolean;
  creatives: Creative[];
  isApproveLoading: boolean;
  isRejectLoading: boolean;

  onUpdateStatusByIdClick: (payload: { action: StatusAction; creativeId: string }) => void;
  onStatusOptionClick: (payload: { action: StatusAction; option: string }) => void;
  onUpdateStatusConfirm: () => void;
  onRejectModalClose: () => void;
  isShowRejectModal: boolean;
  setIsShowRejectModal: (isShowRejectModal: boolean) => void;
  rejectReason: string;
  setRejectReason: (rejectReason: string) => void;
  setClickedStatusId: (clickedStatusId: StatusAction['id']) => void;
  setIsAll: (isAll: boolean) => void;
  clickedStatusId: StatusAction['id'] | null;
  isAll: boolean;
  updatedCreative: Creative | null;
  setUpdatedCreative: (updatedCreative: Creative | null) => void;

  creativeActions: StatusAction[];
  setCreativeActions: (creativeActions: StatusAction[]) => void;
}
const messageSuccess = {
  '2': 'The creative has been approved successfully',
  '3': 'The creative has been rejected successfully',
};

export const CreativeTemplatesCtx = createContext<CreativeTemplatesCtxType>({
  loading: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  selectedCreative: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedCreative: () => {},
  creatives: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onUpdateStatusByIdClick: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onUpdateStatusConfirm: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onStatusOptionClick: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onRejectModalClose: () => {},
  isShowRejectModal: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setIsShowRejectModal: () => {},
  rejectReason: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setRejectReason: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setClickedStatusId: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setIsAll: () => {},
  clickedStatusId: null,
  isAll: false,
  updatedCreative: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setUpdatedCreative: () => {},
  isApproveLoading: false,
  isRejectLoading: false,
  creativeActions: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCreativeActions: () => {},
});

interface CreativeTemplatesProviderProps {
  children: ReactNode;
}

export const CreativeTemplatesProvider: React.FC<CreativeTemplatesProviderProps> = ({
  children,
}) => {
  const [clickedStatusId, setClickedStatusId] = useState<StatusAction['id'] | null>(null);
  const [selectedCreative, setSelectedCreative] = useState<Creative | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [isShowRejectModal, setIsShowRejectModal] = useState(false);
  const [creativeActions, setCreativeActions] = useState<StatusAction[]>([]);
  const { eventId } = useParams() as { eventId: string };
  const { data: creativesData, isLoading } = useGetCreativesQuery({ eventId: eventId });

  const [isAll, setIsAll] = useState(false);
  const [updatedCreative, setUpdatedCreative] = useState<Creative | null>(null);
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const [updateStatusById] = usePatchCreativeStatusByIdMutation();
  const [updateStatusAll] = usePatchCreativeStatusAllMutation();

  // Use RTK Query data directly instead of copying to local state
  const creatives = creativesData?.data || [];

  useEffect(() => {
    if (!selectedCreative && creatives.length) {
      setSelectedCreative(creatives[0]);
      setCreativeActions(creatives[0].status.actions);
    }
    if (selectedCreative && updatedCreative && creatives.length) {
      const nextCreative = findNextCreative(creatives, updatedCreative);
      if (nextCreative) {
        setTimeout(() => {
          setUpdatedCreative(null);
          setSelectedCreative(nextCreative);
          setCreativeActions(nextCreative.status.actions);
        }, 2100);
      }
    }
  }, [creatives, selectedCreative, updatedCreative]);

  // const onStatusUpdateSuccess = () => {
  //     if (creativesData?.data?.length && selectedCreative) {
  //         //update selected creative to next from list
  //         const next = findNextCreative(creativesData.data, selectedCreative)
  //         next && setSelectedCreative(next)
  //     }
  // }

  const onUpdateStatusByIdClick = async ({
    action,
    creativeId,
  }: {
    action: StatusAction;
    creativeId: string;
  }) => {
    const payload = {
      eventId,
      creativeId,
      status: action.id,
      transition: action.transition,
    };
    if (action.id.toString() === '3') {
      return setClickedStatusId(action.id);
    }

    try {
      const res = await updateStatusById(payload);
      action.id.toString() === '2' ? setIsApproveLoading(true) : setIsRejectLoading(true);
      if ('error' in res) {
        console.log(res.error);
        return notification.open({
          type: 'error',
          placement: 'bottomLeft',
          closeIcon: false,
          message:
            ((res as ErrorDataResponse)?.error?.data?.message as string) ||
            'Error updating creative status',
        });
      }
      if (res?.data?.data) {
        setUpdatedCreative(res.data.data);
        setCreativeActions(res.data.data.status.actions);
        notification.open({
          type: 'success',
          placement: 'bottomLeft',
          closeIcon: false,
          message: messageSuccess[action.id.toString() as keyof typeof messageSuccess],
        });
        // onStatusUpdateSuccess()
        return onRejectModalClose();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsApproveLoading(false);
      setIsRejectLoading(false);
    }
  };

  const onStatusOptionClick = async ({
    action,
    option,
  }: {
    action: StatusAction;
    option: string;
  }) => {
    option.includes('all') ? setIsAll(true) : setIsAll(false);
    console.log(option);
    if (action.id.toString() === '3') {
      return setClickedStatusId(action.id);
    }
    if (!selectedCreative) return;

    try {
      action.id.toString() === '2' ? setIsApproveLoading(true) : setIsRejectLoading(true);
      const payload = {
        eventId,
        status: action.id,
        creativeId: selectedCreative.id,
        transition: action.transition,
      };
      const res = option.includes('all')
        ? await updateStatusAll(payload)
        : await updateStatusById(payload);
      if ('error' in res) {
        return notification.open({
          type: 'error',
          placement: 'bottomLeft',
          closeIcon: false,
          message:
            ((res as ErrorDataResponse)?.error?.data?.message as string) ||
            'Error updating audience status',
        });
      }
      if ('data' in res) {
        notification.open({
          type: 'success',
          placement: 'bottomLeft',
          closeIcon: false,
          message: messageSuccess[action.id.toString() as keyof typeof messageSuccess],
        });
        onRejectModalClose();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsApproveLoading(false);
      setIsRejectLoading(false);
    }
  };

  const onUpdateStatusConfirm = async () => {
    if (!selectedCreative?.id || !clickedStatusId) return;
    const currentAction = creativeActions.find((item) => item.id === clickedStatusId);
    if (!currentAction) {
      return;
    }
    const payload = {
      eventId,
      creativeId: selectedCreative?.id,
      status: clickedStatusId,
      comment: rejectReason,
      transition: currentAction.transition,
    };
    try {
      if (isAll) {
        currentAction.id.toString() === '2' ? setIsApproveLoading(true) : setIsRejectLoading(true);
        await onStatusOptionClick({ action: currentAction, option: 'all' });
      } else {
        const res = await updateStatusById(payload);
        if ('data' in res) {
          setUpdatedCreative(res.data.data);
          setCreativeActions(res.data.data.status.actions);
          notification.open({
            type: 'success',
            placement: 'bottomLeft',
            closeIcon: false,
            message: messageSuccess[clickedStatusId.toString() as keyof typeof messageSuccess],
          });
        }
      }
      onRejectModalClose();
    } catch (error) {
      console.log(error);
    } finally {
      setIsApproveLoading(false);
      setIsRejectLoading(false);
    }
  };

  const onRejectModalClose = () => {
    setIsShowRejectModal(false);
    setRejectReason('');
    setClickedStatusId(null);
  };

  return (
    <CreativeTemplatesCtx.Provider
      value={{
        loading: isLoading,
        selectedCreative,
        setSelectedCreative,
        creatives,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onUpdateStatusByIdClick,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onStatusOptionClick,
        onRejectModalClose,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onUpdateStatusConfirm,
        isShowRejectModal,
        setIsShowRejectModal,
        rejectReason,
        setRejectReason,
        setClickedStatusId,
        setIsAll,
        clickedStatusId,
        isAll,
        updatedCreative,
        setUpdatedCreative,
        isApproveLoading,
        isRejectLoading,
        creativeActions,
        setCreativeActions,
      }}
    >
      {children}
    </CreativeTemplatesCtx.Provider>
  );
};
